import { useQuery } from '@tanstack/react-query'
import { Check, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/utils/cn'
import { contactsQuery } from '../api'
import { roleLabel } from '../format'
import type { Contact } from '../types'

interface ContactPickerProps {
  selected: Contact[]
  onToggle: (contact: Contact) => void
  /** Hide people already in the conversation. */
  exclude?: number[]
  multiple: boolean
}

/** Searchable list of the people the user may write to (the server applies the rules). */
export function ContactPicker({ selected, onToggle, exclude = [], multiple }: ContactPickerProps) {
  const { t } = useTranslation('messaging')
  const [q, setQ] = useState('')
  const debounced = useDebouncedValue(q.trim(), 250)
  const contacts = useQuery(contactsQuery(debounced))
  const rows = (contacts.data ?? []).filter((c) => !exclude.includes(c.id))

  return (
    <div>
      <label className="relative block">
        <span className="sr-only">{t('Search people')}</span>
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-subtle"
          aria-hidden="true"
        />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('Search by name, class or email…')}
          className="h-10 w-full rounded-control border border-line bg-surface pr-3 pl-9 text-sm text-ink placeholder:text-subtle focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none"
        />
      </label>
      <div className="mt-3 max-h-80 overflow-y-auto rounded-control border border-line">
        {contacts.isPending ? (
          <div className="grid place-items-center py-8">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted">
            {debounced ? t('Nobody matches “{{q}}”.', { q: debounced }) : t('Nobody to write to yet.')}
          </p>
        ) : (
          <ul role="listbox" aria-multiselectable={multiple} aria-label={t('People')}>
            {rows.map((c) => {
              const on = selected.some((s) => s.id === c.id)
              return (
                <li key={c.id} role="option" aria-selected={on}>
                  <button
                    type="button"
                    onClick={() => onToggle(c)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-muted',
                      on && 'bg-brand-50/70',
                    )}
                  >
                    <Avatar name={c.name} src={c.avatarUrl} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{c.name}</span>
                      <span className="block truncate text-xs text-muted">
                        {roleLabel(c.role)}
                        {c.detail ? ` · ${c.detail}` : ''}
                      </span>
                    </span>
                    {multiple ? (
                      <span
                        className={cn(
                          'grid size-5 place-items-center rounded border',
                          on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line-strong',
                        )}
                        aria-hidden="true"
                      >
                        {on ? <Check className="size-3.5" /> : null}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
